import { Component, Input, OnInit } from '@angular/core'
import { CraftEnvironmentService } from 'framework/craft-environment/craft-environment.service'
import { LocaleService } from 'framework/locale/services/locale.service'
import { AuthenticationService } from 'framework/authentication/services/authentication.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

interface IconItem {
    text: string
    href: string
    current: boolean
    icon: string
}

interface ImageItem {
    text: string
    href: string
    current: boolean
    img: string
}

interface SocialMediaItems {
    newsletter: string
    facebook: string
    instagram: string
    linkedin: string
    twitter: string
}

@UntilDestroy()
@Component({
    selector: 'mg-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
    craftEnvironment: string
    languages: string[]
    currentLanguage: string
    containsMultipleLocales: boolean = false
    homeUrl: string = '/'
    originUrl: string = ''
    remainingPath: string = ''
    iconItems: IconItem[] = []
    imageItems: ImageItem[] = []
    socialMedia: SocialMediaItems
    isMenuOpen: boolean = false

    @Input() useGreenNav: boolean = false

    constructor(
        private craftEnvironmentService: CraftEnvironmentService,
        private localeService: LocaleService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit() {
        this.craftEnvironmentService.craftEnvironment$.pipe(untilDestroyed(this)).subscribe(craftEnvironment => {
            this.craftEnvironment = craftEnvironment
        })

        this.craftEnvironmentService.languages$.pipe(untilDestroyed(this)).subscribe(languageObject => {
            this.languages = languageObject?.languages || [this.localeService.language]
            this.currentLanguage = this.localeService.language
        })

        if (typeof window !== 'undefined') {
            this.originUrl = window.location.origin
            this.remainingPath = window.location.pathname.slice(3) + window.location.search

            if (window.location.pathname.match(/^\/\w{2}\//)) {
                this.containsMultipleLocales = true
                this.homeUrl = `${this.originUrl}/${this.currentLanguage}`
            } else {
                this.homeUrl = this.originUrl
            }

            fetch('https://cms.mistergreen.club/api', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: `
            query getMenuItems($sites: [String]) {
              globalSets(site: $sites) {
                __typename
                id
                ... on menu_GlobalSet {
                  menuItems {
                    ... on menuItems_BlockType {
                      linkArray {
                        ... on linkArray_internalLink_BlockType {
                          text
                          entry {
                            typeHandle
                            slug
                          }
                        }
                        ... on linkArray_externalLink_BlockType {
                          text
                          linkUrl
                        }
                      }
                      displayType
                      icon
                      image {
                        filename
                        dateUpdated
                        height
                        width
                      }
                    }
                  }
                }
                ... on sitewideSettings_GlobalSet {
                  newsletterSignupUrl
                  facebookUrl
                  twitterUrl
                  instagramUrl
                  linkedinUrl
                }
              }
            }
          `,
                    variables: {
                        sites: [this.craftEnvironment],
                    },
                }),
            })
                .then(res => res.json())
                .then(res => {
                    if (res.data.globalSets) {
                        // Menu
                        const menuSets = res.data.globalSets.filter(set => set.__typename === 'menu_GlobalSet')
                        if (menuSets[0] && menuSets[0].menuItems && menuSets[0].menuItems.length) {
                            menuSets[0].menuItems.forEach(item => {
                                const slug =
                                    item.linkArray[0].entry && item.linkArray[0].entry[0]
                                        ? item.linkArray[0].entry[0].slug
                                        : item.linkArray[0].linkUrl
                                let href = ''

                                if (item.__typename === 'homepage_homepage_Entry') {
                                    href = this.homeUrl
                                } else if (this.containsMultipleLocales) {
                                    href = `${this.originUrl}/${this.currentLanguage}/${slug}`
                                } else {
                                    href = `${this.originUrl}/${slug}`
                                }

                                const current = item.__typename === 'occasions_occasions_Entry'

                                if (item.displayType === 'inAdditionalMenuWithImage') {
                                    this.imageItems = [
                                        ...this.imageItems,
                                        {
                                            text: item.linkArray[0].text,
                                            href,
                                            current,
                                            img:
                                                item.image && item.image[0]
                                                    ? `https://static.mistergreen.nl/${item.image[0].filename.slice(
                                                          0,
                                                          item.image[0].filename.lastIndexOf('.')
                                                      )}_small_2x.jpeg?v=${item.image[0].dateUpdated}`
                                                    : '',
                                        },
                                    ]
                                } else {
                                    this.iconItems = [
                                        ...this.iconItems,
                                        {
                                            text: item.linkArray[0].text,
                                            href,
                                            current,
                                            icon: item.icon,
                                        },
                                    ]
                                }
                            })
                        }

                        // Social media links
                        const sitewideSets = res.data.globalSets.filter(
                            set => set.__typename === 'sitewideSettings_GlobalSet'
                        )
                        if (sitewideSets[0]) {
                            this.socialMedia = {
                                newsletter: sitewideSets[0].newsletterSignupUrl || '',
                                facebook: sitewideSets[0].facebookUrl || '',
                                instagram: sitewideSets[0].instagramUrl || '',
                                twitter: sitewideSets[0].twitterUrl || '',
                                linkedin: sitewideSets[0].linkedinUrl || '',
                            }
                        }
                    }
                })
        }

        window.addEventListener(
            'click',
            event => {
                if (
                    this.isMenuOpen &&
                    !(event.target as HTMLElement).closest('.header__additional-menu-inner-container') &&
                    !(event.target as HTMLElement).closest('.header__menu-button')
                ) {
                    this.setMenuState(false)
                }
            },
            false
        )
    }

    setMenuState(open: boolean) {
        this.isMenuOpen = open
    }

    logout() {
        this.authenticationService.logOut()
        if (window.location.pathname.replace('/', '') === 'account') {
            window.location.pathname = '/'
        } else {
            window.location.reload()
        }
    }
}
